import { S as AssetStoreProvider, a as MENU_LABELS, c as mergeMenus, d as PageBackgroundFields, i as OutlinePane, l as pruneMenus, n as Ruler, o as MENU_ORDER, r as RulerMarker, s as group, t as ShortcutSheet, u as MenuBar } from "./ShortcutSheet-D-ElFRSm.mjs";
import { useState } from "react";
import { DEFAULT_PAGE_SETUP, NO_PAGE_BACKGROUND, PAGE_SIZES, PAGE_SIZE_IDS, createPageGeometry, inchToPt, ptToInch } from "@burfy/docs-core";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/ui/PageSetupDialog.tsx
/**
* Page size, orientation, margins and the header/footer bands.
*
* Margins are shown in inches because that is how people think about paper,
* while everything is stored in points. The conversion happens only at this
* boundary — a layout model that carried display units would eventually
* compare a value in inches with one in points and be wrong by a factor of 72.
*/
const MARGIN_FIELDS = [
	{
		key: "topPt",
		label: "Top"
	},
	{
		key: "bottomPt",
		label: "Bottom"
	},
	{
		key: "leftPt",
		label: "Left"
	},
	{
		key: "rightPt",
		label: "Right"
	}
];
function PageSetupDialog({ value, onApply, onCancel }) {
	const [draft, setDraft] = useState(value);
	let error = null;
	try {
		createPageGeometry(draft);
	} catch (caught) {
		error = caught instanceof Error ? caught.message : "This page setup is not usable";
	}
	const sizeId = draft.size.kind === "preset" ? draft.size.id : "custom";
	const setMargin = (key, inches) => setDraft((current) => ({
		...current,
		margins: {
			...current.margins,
			[key]: inchToPt(inches)
		}
	}));
	return /* @__PURE__ */ jsx("div", {
		className: "burfy-docs-dialog-backdrop",
		onClick: onCancel,
		role: "presentation",
		children: /* @__PURE__ */ jsxs("div", {
			className: "burfy-docs-dialog",
			role: "dialog",
			"aria-modal": "true",
			"aria-label": "Page setup",
			onClick: (event) => event.stopPropagation(),
			children: [
				/* @__PURE__ */ jsx("h2", {
					className: "burfy-docs-dialog-title",
					children: "Page setup"
				}),
				/* @__PURE__ */ jsxs("label", {
					className: "burfy-docs-field",
					children: [/* @__PURE__ */ jsx("span", { children: "Paper size" }), /* @__PURE__ */ jsxs("select", {
						value: sizeId,
						onChange: (event) => {
							const next = event.target.value;
							setDraft((current) => ({
								...current,
								size: next === "custom" ? {
									kind: "custom",
									widthPt: 595.28,
									heightPt: 841.89
								} : {
									kind: "preset",
									id: next
								}
							}));
						},
						children: [PAGE_SIZE_IDS.map((id) => /* @__PURE__ */ jsx("option", {
							value: id,
							children: PAGE_SIZES[id].label
						}, id)), /* @__PURE__ */ jsx("option", {
							value: "custom",
							children: "Custom"
						})]
					})]
				}),
				draft.size.kind === "custom" && /* @__PURE__ */ jsxs("div", {
					className: "burfy-docs-field-row",
					children: [/* @__PURE__ */ jsxs("label", {
						className: "burfy-docs-field",
						children: [/* @__PURE__ */ jsx("span", { children: "Width (in)" }), /* @__PURE__ */ jsx("input", {
							type: "number",
							min: "1",
							step: "0.25",
							value: round(ptToInch(draft.size.widthPt)),
							onChange: (event) => setDraft((current) => current.size.kind === "custom" ? {
								...current,
								size: {
									...current.size,
									widthPt: inchToPt(Number(event.target.value))
								}
							} : current)
						})]
					}), /* @__PURE__ */ jsxs("label", {
						className: "burfy-docs-field",
						children: [/* @__PURE__ */ jsx("span", { children: "Height (in)" }), /* @__PURE__ */ jsx("input", {
							type: "number",
							min: "1",
							step: "0.25",
							value: round(ptToInch(draft.size.heightPt)),
							onChange: (event) => setDraft((current) => current.size.kind === "custom" ? {
								...current,
								size: {
									...current.size,
									heightPt: inchToPt(Number(event.target.value))
								}
							} : current)
						})]
					})]
				}),
				/* @__PURE__ */ jsxs("fieldset", {
					className: "burfy-docs-fieldset",
					children: [/* @__PURE__ */ jsx("legend", { children: "Orientation" }), ["portrait", "landscape"].map((orientation) => /* @__PURE__ */ jsxs("label", {
						className: "burfy-docs-radio",
						children: [/* @__PURE__ */ jsx("input", {
							type: "radio",
							name: "orientation",
							value: orientation,
							checked: draft.orientation === orientation,
							onChange: () => setDraft((current) => ({
								...current,
								orientation
							}))
						}), /* @__PURE__ */ jsx("span", { children: orientation === "portrait" ? "Portrait" : "Landscape" })]
					}, orientation))]
				}),
				/* @__PURE__ */ jsxs("fieldset", {
					className: "burfy-docs-fieldset",
					children: [/* @__PURE__ */ jsx("legend", { children: "Margins (inches)" }), /* @__PURE__ */ jsx("div", {
						className: "burfy-docs-field-row",
						children: MARGIN_FIELDS.map(({ key, label }) => /* @__PURE__ */ jsxs("label", {
							className: "burfy-docs-field",
							children: [/* @__PURE__ */ jsx("span", { children: label }), /* @__PURE__ */ jsx("input", {
								type: "number",
								min: "0",
								step: "0.25",
								value: round(ptToInch(draft.margins[key])),
								onChange: (event) => setMargin(key, Number(event.target.value))
							})]
						}, key))
					})]
				}),
				/* @__PURE__ */ jsxs("fieldset", {
					className: "burfy-docs-fieldset",
					children: [
						/* @__PURE__ */ jsx("legend", { children: "Header and footer" }),
						/* @__PURE__ */ jsxs("label", {
							className: "burfy-docs-radio",
							children: [/* @__PURE__ */ jsx("input", {
								type: "checkbox",
								checked: draft.header.enabled,
								onChange: (event) => setDraft((current) => ({
									...current,
									header: {
										...current.header,
										enabled: event.target.checked
									}
								}))
							}), /* @__PURE__ */ jsx("span", { children: "Header band" })]
						}),
						/* @__PURE__ */ jsxs("label", {
							className: "burfy-docs-radio",
							children: [/* @__PURE__ */ jsx("input", {
								type: "checkbox",
								checked: draft.footer.enabled,
								onChange: (event) => setDraft((current) => ({
									...current,
									footer: {
										...current.footer,
										enabled: event.target.checked
									}
								}))
							}), /* @__PURE__ */ jsx("span", { children: "Footer band with page numbers" })]
						})
					]
				}),
				/* @__PURE__ */ jsxs("fieldset", {
					className: "burfy-docs-fieldset",
					children: [
						/* @__PURE__ */ jsx("legend", { children: "Content" }),
						/* @__PURE__ */ jsxs("label", {
							className: "burfy-docs-field",
							children: [/* @__PURE__ */ jsx("span", { children: "Vertical position" }), /* @__PURE__ */ jsxs("select", {
								className: "burfy-docs-tb-select",
								"aria-label": "Vertical position of the page content",
								value: draft.verticalAlign ?? "top",
								onChange: (event) => setDraft((current) => ({
									...current,
									verticalAlign: event.target.value
								})),
								children: [
									/* @__PURE__ */ jsx("option", {
										value: "top",
										children: "Top of the page"
									}),
									/* @__PURE__ */ jsx("option", {
										value: "middle",
										children: "Centred on the page"
									}),
									/* @__PURE__ */ jsx("option", {
										value: "bottom",
										children: "Bottom of the page"
									})
								]
							})]
						}),
						/* @__PURE__ */ jsx("p", {
							className: "burfy-docs-dialog-note",
							children: "Where the text rests inside each page. It moves the content within a page; it never changes where the pages break."
						})
					]
				}),
				/* @__PURE__ */ jsx(PageBackgroundFields, {
					value: draft.background ?? NO_PAGE_BACKGROUND,
					onChange: (background) => setDraft((current) => ({
						...current,
						background
					}))
				}),
				error && /* @__PURE__ */ jsx("p", {
					className: "burfy-docs-dialog-error",
					role: "alert",
					children: "These margins leave no room for text on the page."
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "burfy-docs-dialog-actions",
					children: [
						/* @__PURE__ */ jsx("button", {
							type: "button",
							onClick: () => setDraft(DEFAULT_PAGE_SETUP),
							children: "Reset"
						}),
						/* @__PURE__ */ jsx("span", { className: "burfy-docs-dialog-spacer" }),
						/* @__PURE__ */ jsx("button", {
							type: "button",
							onClick: onCancel,
							children: "Cancel"
						}),
						/* @__PURE__ */ jsx("button", {
							type: "button",
							className: "burfy-docs-dialog-primary",
							disabled: error !== null,
							onClick: () => onApply(draft),
							children: "Apply"
						})
					]
				})
			]
		})
	});
}
/** Two decimals is the finest anyone sets a margin to by hand. */
function round(inches) {
	return Math.round(inches * 100) / 100;
}
//#endregion
export { AssetStoreProvider, MENU_LABELS, MENU_ORDER, MenuBar, OutlinePane, PageSetupDialog, Ruler, RulerMarker, ShortcutSheet, group, mergeMenus, pruneMenus };
